/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect } from 'react';
import { Panel, PanelBody, PanelHeader } from '../../../util/panel';
import { Button, Card, Input, Select, Table, Spin } from 'antd';
import { FaSearch, FaGift, FaPlus, FaMinus } from 'react-icons/fa';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import fitMissionServices from '../../../../services/fit-mission.services';
import LegacyUserRewardModal from '../components/modal/LegacyUserRewardModal';
import MemberInfoCard from '../../checking-branch/components/MemberInfoCard';

dayjs.locale('th');

const allMonthOptions = [
  { value: 1, label: 'มกราคม' },
  { value: 2, label: 'กุมภาพันธ์' },
  { value: 3, label: 'มีนาคม' },
  { value: 4, label: 'เมษายน' },
  { value: 5, label: 'พฤษภาคม' },
  { value: 6, label: 'มิถุนายน' },
  { value: 7, label: 'กรกฎาคม' },
  { value: 8, label: 'สิงหาคม' },
  { value: 9, label: 'กันยายน' },
  { value: 10, label: 'ตุลาคม' },
  { value: 11, label: 'พฤศจิกายน' },
  { value: 12, label: 'ธันวาคม' },
];

// ปีเริ่มจาก 2026 ถึงปีปัจจุบัน
const generateYearOptions = () => {
  const startYear = 2026;
  const currentYear = dayjs().year();
  const years = [];
  for (let year = startYear; year <= currentYear; year++) {
    years.push({ value: year, label: `${year}` });
  }
  return years;
};

const LegacyMissionRedeemPage = () => {
  const [memberCode, setMemberCode] = useState('');
  const [missionUser, setMissionUser] = useState(null);
  const [missionPointInfo, setMissionPointInfo] = useState(null);
  const [userRewardModalOpen, setUserRewardModalOpen] = useState(false);

  // Form สำหรับค้นหาผู้ใช้
  const searchUserForm = useForm({
    defaultValues: {
      memberCode: '',
    },
  });
  const { watch: watchSearchUser } = searchUserForm;
  const searchMemberCode = watchSearchUser('memberCode');

  // Form สำหรับ filter Point Info
  const pointFilterForm = useForm({
    defaultValues: {
      month: dayjs().month() + 1,
      year: dayjs().year(),
    },
  });
  const { watch: watchPointFilter, setValue: setPointFilterValue } =
    pointFilterForm;
  const selectedMonth = watchPointFilter('month');
  const selectedYear = watchPointFilter('year');

  // Year options: 2026 to current year
  const yearOptions = useMemo(() => generateYearOptions(), []);

  // Month options: filter based on selected year
  const monthOptions = useMemo(() => {
    const currentYear = dayjs().year();
    const currentMonth = dayjs().month() + 1;

    // ถ้าเลือกปีปัจจุบัน ให้แสดงเฉพาะเดือนที่ไม่เกินเดือนปัจจุบัน
    if (selectedYear === currentYear) {
      return allMonthOptions.filter((option) => option.value <= currentMonth);
    }
    // ถ้าเลือกปีอื่น แสดงทั้ง 12 เดือน
    return allMonthOptions;
  }, [selectedYear]);

  // Reset month when year changes and selected month is not available
  useEffect(() => {
    const currentYear = dayjs().year();
    const currentMonth = dayjs().month() + 1;

    // ถ้าเลือกปีปัจจุบันและเดือนที่เลือกมากกว่าเดือนปัจจุบัน ให้ reset เป็นเดือนปัจจุบัน
    if (selectedYear === currentYear && selectedMonth > currentMonth) {
      setPointFilterValue('month', currentMonth);
    }
  }, [selectedYear, selectedMonth, setPointFilterValue]);

  // Form สำหรับแลกรางวัล
  const userRewardForm = useForm({
    defaultValues: {
      missionUserId: '',
      missionRewardId: '',
      quantity: 1,
      title: '',
      description: '',
      image: '',
    },
  });

  // Search Mission User by Member Code
  const { isFetching: isFetchingMissionUser, refetch: refetchMissionUser } =
    fitMissionServices.useQueryGetMissionUserByMemberCode(
      { memberCode },
      (data) => {
        if (data) {
          setMissionUser(data);
        } else {
          setMissionUser(null);
          setMissionPointInfo(null);
        }
      },
    );

  // Get Mission Point Info
  const { isFetching: isFetchingPointInfo } =
    fitMissionServices.useQueryGetMissionPointInfoByMissionUserId(
      {
        missionUserId: missionUser?.id,
        month: selectedMonth,
        year: selectedYear,
      },
      (data) => {
        setMissionPointInfo(data);
      },
    );

  const handleSearch = () => {
    if (searchMemberCode?.trim()) {
      setMemberCode(searchMemberCode.trim());
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleOpenUserRewardModal = () => {
    if (missionUser) {
      userRewardForm.setValue('missionUserId', missionUser.id);
      setUserRewardModalOpen(true);
    }
  };

  const handleCloseUserRewardModal = () => {
    userRewardForm.reset();
    setUserRewardModalOpen(false);
  };

  const pointHistoryColumns = [
    {
      title: 'วันที่ เวลา',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (_, record) => (
        <span>{dayjs(record.createdAt).format('DD MMM YYYY HH:mm')}</span>
      ),
    },
    {
      title: 'คะแนน',
      dataIndex: 'point',
      key: 'point',
      width: 80,
      render: (_, record) => (
        <span
          style={{
            color: record.type === 'increase' ? '#52c41a' : '#ff4d4f',
            fontWeight: 'bold',
          }}
        >
          {record.type === 'increase' ? '+' : '-'}
          {record.point}
        </span>
      ),
    },
    {
      title: 'รายละเอียด',
      dataIndex: 'description',
      key: 'description',
      render: (_, record) => <span>{record.description || '-'}</span>,
    },
    {
      title: 'สินค้าแลกคะแนน',
      dataIndex: ['missionUserReward', 'title'],
      key: 'reward',
      render: (_, record) => (
        <span>{record?.missionUserReward?.missionReward?.title || '-'}</span>
      ),
    },
  ];

  return (
    <Panel>
      <PanelHeader>แลกของรางวัล</PanelHeader>
      <PanelBody style={{ position: 'relative' }}>
        <div style={{ display: 'flex', gap: 24, height: '100%' }}>
          {/* Left Panel - Search User */}
          <Card
            title="ค้นหาผู้ใช้"
            style={{ width: 350, flexShrink: 0, height: 'fit-content' }}
          >
            <FormProvider {...searchUserForm}>
              <div style={{ marginBottom: 16 }}>
                <Controller
                  name="memberCode"
                  render={({ field: { value, onChange } }) => (
                    <Input
                      value={value}
                      onChange={(e) => {
                        const cleaned =
                          typeof e.target.value === 'string'
                            ? e.target.value.replace(/[^\d]/g, '')
                            : e.target.value;

                        if (!cleaned) {
                          onChange('');
                        } else {
                          onChange(cleaned);
                        }
                      }}
                      placeholder="กรอกรหัสสมาชิก"
                      prefix={<FaSearch />}
                      onKeyDown={handleKeyPress}
                      allowClear
                    />
                  )}
                />
              </div>
              <Button
                type="primary"
                onClick={handleSearch}
                loading={isFetchingMissionUser}
                block
              >
                ค้นหา
              </Button>
            </FormProvider>

            {isFetchingMissionUser && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <Spin />
              </div>
            )}

            {missionUser && !isFetchingMissionUser && (
              <div style={{ marginTop: 24 }}>
                <MemberInfoCard
                  user={missionUser.user}
                  partner={missionUser.partner}
                />
              </div>
            )}
          </Card>

          {/* Right Panel - Point Info & History */}
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            {missionUser ? (
              <>
                {/* Filter Section */}
                <Card style={{ marginBottom: 16 }}>
                  <FormProvider {...pointFilterForm}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        flexWrap: 'wrap',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span>เดือน:</span>
                        <Controller
                          name="month"
                          render={({ field }) => (
                            <Select {...field} style={{ width: 120 }}>
                              {monthOptions.map((option) => (
                                <Select.Option
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </Select.Option>
                              ))}
                            </Select>
                          )}
                        />
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span>ปี:</span>
                        <Controller
                          name="year"
                          render={({ field }) => (
                            <Select {...field} style={{ width: 100 }}>
                              {yearOptions.map((option) => (
                                <Select.Option
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </Select.Option>
                              ))}
                            </Select>
                          )}
                        />
                      </div>
                      <div style={{ flex: 1 }} />
                      <Button
                        type="primary"
                        icon={<FaGift style={{ marginRight: 8 }} />}
                        onClick={handleOpenUserRewardModal}
                      >
                        แลกคะแนน
                      </Button>
                    </div>
                  </FormProvider>
                </Card>

                {/* Point Summary Card */}
                <Card style={{ marginBottom: 16 }}>
                  <Spin spinning={isFetchingPointInfo}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 32,
                      }}
                    >
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '16px 32px',
                          background: '#f0f5ff',
                          borderRadius: 8,
                        }}
                      >
                        <div style={{ fontSize: 14, color: '#666' }}>
                          คะแนนปัจจุบัน
                        </div>
                        <div
                          style={{
                            fontSize: 32,
                            fontWeight: 'bold',
                            color: '#1890ff',
                          }}
                        >
                          {missionPointInfo?.remainPoint || 0}
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 4, color: '#666' }}>
                          เดือน{' '}
                          {
                            monthOptions.find((m) => m.value === selectedMonth)
                              ?.label
                          }{' '}
                          {selectedYear}
                        </div>
                        <div style={{ fontSize: 12, color: '#999' }}>
                          รหัสสมาชิก: {missionUser.memberCode}
                        </div>
                      </div>
                    </div>
                  </Spin>
                </Card>

                {/* Point History Table */}
                <Card title="ประวัติคะแนน">
                  <Spin spinning={isFetchingPointInfo}>
                    <Table
                      columns={pointHistoryColumns}
                      dataSource={missionPointInfo?.pointHistory || []}
                      rowKey="id"
                      scroll={{ x: 'max-content' }}
                      locale={{
                        emptyText: 'ไม่มีประวัติคะแนน',
                      }}
                    />
                  </Spin>
                </Card>
              </>
            ) : (
              <Card>
                <div
                  style={{
                    textAlign: 'center',
                    padding: 48,
                    color: '#999',
                  }}
                >
                  <FaSearch style={{ fontSize: 48, marginBottom: 16 }} />
                  <div>กรุณาค้นหาผู้ใช้จากรหัสสมาชิก</div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </PanelBody>

      <FormProvider {...userRewardForm}>
        <LegacyUserRewardModal
          open={userRewardModalOpen}
          handleClose={handleCloseUserRewardModal}
          memberCode={memberCode}
          missionUser={missionUser}
          month={selectedMonth}
          year={selectedYear}
          onSuccess={() => {
            refetchMissionUser();
          }}
        />
      </FormProvider>
    </Panel>
  );
};

export default LegacyMissionRedeemPage;
